import React, { useState, useEffect } from 'react';
import api from '../../utils/api';
import Card from '../UI/Card';
import Button from '../UI/Button';
import Input from '../UI/Input';
import Modal from '../UI/Modal';

export default function TemplateManager() {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
  });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchTemplates();
  }, []);

  const fetchTemplates = async () => {
    try {
      setLoading(true);
      const response = await api.get('/api/records/templates/my');
      setTemplates(response.data.templates || []);
    } catch (err) {
      console.error('Failed to fetch templates:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: null
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const newErrors = {};
    if (!formData.title.trim()) {
      newErrors.title = '템플릿 제목을 입력해주세요.';
    }
    if (!formData.description.trim()) {
      newErrors.description = '템플릿 설명을 입력해주세요.';
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    try {
      setSubmitting(true);
      await api.post('/api/records/templates', formData);

      setShowCreateModal(false);
      setFormData({ title: '', description: '' });
      fetchTemplates();
    } catch (err) {
      setErrors({ submit: err.response?.data?.error || '템플릿 생성에 실패했습니다.' });
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (templateId) => {
    if (!window.confirm('정말 삭제하시겠습니까?')) return;

    try {
      await api.delete(`/api/records/templates/${templateId}`);
      fetchTemplates();
    } catch (err) {
      alert(err.response?.data?.error || '삭제에 실패했습니다.');
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-neutral-800">활동 템플릿</h2>
          <p className="text-neutral-600 mt-1">자주 사용하는 활동 설명을 템플릿으로 저장하세요.</p>
        </div>
        <Button onClick={() => setShowCreateModal(true)}>
          새 템플릿 만들기
        </Button>
      </div>

      {templates.length === 0 ? (
        <Card className="text-center py-12">
          <p className="text-neutral-500 mb-4">아직 생성한 템플릿이 없습니다.</p>
          <Button onClick={() => setShowCreateModal(true)}>
            첫 템플릿 만들기
          </Button>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {templates.map((template) => (
            <Card key={template.id} className="animate-fade-in">
              <div className="flex items-start justify-between mb-3">
                <h3 className="font-bold text-neutral-800 flex-1">{template.title}</h3>
                <button
                  onClick={() => handleDelete(template.id)}
                  className="text-danger-500 hover:text-danger-600 text-sm"
                >
                  삭제
                </button>
              </div>

              <p className="text-neutral-700 mb-4 whitespace-pre-wrap">
                {template.description}
              </p>

              <div className="flex items-center justify-between text-sm text-neutral-500 pt-3 border-t border-neutral-100">
                <span>
                  {new Date(template.createdAt).toLocaleDateString('ko-KR')}
                </span>
                {template._count && (
                  <span>{template._count.records || 0}회 사용</span>
                )}
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Create Modal */}
      <Modal
        isOpen={showCreateModal}
        onClose={() => {
          setShowCreateModal(false);
          setFormData({ title: '', description: '' });
          setErrors({});
        }}
        title="새 템플릿 만들기"
      >
        <form onSubmit={handleSubmit} className="space-y-6">
          <Input
            label="템플릿 제목"
            name="title"
            value={formData.title}
            onChange={handleChange}
            error={errors.title}
            placeholder="예: AI 학습 활동"
            required
          />

          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-2">
              활동 설명
              <span className="text-danger-500 ml-1">*</span>
            </label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleChange}
              className="w-full px-4 py-2.5 rounded-xl border-2 border-neutral-200 focus:border-primary-500 focus:outline-none transition-colors duration-200 min-h-[150px]"
              placeholder="활동 내용을 구체적으로 설명하세요..."
            />
            {errors.description && (
              <p className="mt-1.5 text-sm text-danger-500">{errors.description}</p>
            )}
          </div>

          {errors.submit && (
            <div className="p-4 bg-danger-50 border border-danger-200 rounded-xl">
              <p className="text-sm text-danger-600">{errors.submit}</p>
            </div>
          )}

          <div className="flex gap-3">
            <Button type="submit" disabled={submitting} className="flex-1">
              {submitting ? '생성 중...' : '템플릿 생성'}
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={() => setShowCreateModal(false)}
              disabled={submitting}
            >
              취소
            </Button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
